import { motion } from "framer-motion";

interface SectionSkeletonProps {
  variant?: "default" | "cards" | "faq" | "stats" | "cta";
}

const shimmer = "relative overflow-hidden before:absolute before:inset-0 before:-translate-x-full before:animate-[shimmer_2s_infinite] before:bg-gradient-to-r before:from-transparent before:via-white/20 before:to-transparent";

const SectionSkeleton = ({ variant = "default" }: SectionSkeletonProps) => {
  if (variant === "cards") {
    return (
      <div className="py-28 lg:py-36 bg-cream">
        <div className="max-w-7xl mx-auto px-6">
          {/* Header skeleton */}
          <div className="text-center mb-20">
            <div className={`h-8 w-32 bg-muted/50 rounded-full mx-auto mb-6 ${shimmer}`} />
            <div className={`h-12 w-96 max-w-full bg-muted/40 rounded-lg mx-auto ${shimmer}`} />
          </div>
          {/* Cards grid skeleton */}
          <div className="grid md:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="glass-card p-8 space-y-6">
                <div className={`w-16 h-16 bg-muted/50 rounded-2xl ${shimmer}`} />
                <div className={`h-8 w-32 bg-muted/40 rounded-lg ${shimmer}`} />
                <div className="space-y-2">
                  <div className={`h-4 w-full bg-muted/30 rounded ${shimmer}`} />
                  <div className={`h-4 w-3/4 bg-muted/30 rounded ${shimmer}`} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (variant === "faq") {
    return (
      <div className="py-28 lg:py-36 bg-cream">
        <div className="max-w-4xl mx-auto px-6">
          {/* Header skeleton */}
          <div className="text-center mb-16">
            <div className={`h-8 w-24 bg-muted/50 rounded-full mx-auto mb-6 ${shimmer}`} />
            <div className={`h-12 w-80 max-w-full bg-muted/40 rounded-lg mx-auto mb-4 ${shimmer}`} />
            <div className={`h-6 w-64 bg-muted/30 rounded-lg mx-auto ${shimmer}`} />
          </div>
          {/* FAQ items skeleton */}
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="glass-card px-6 py-5 flex items-center gap-4">
                <div className={`w-12 h-12 bg-muted/50 rounded-2xl flex-shrink-0 ${shimmer}`} />
                <div className={`h-6 flex-1 bg-muted/40 rounded-lg ${shimmer}`} />
                <div className={`w-5 h-5 bg-muted/30 rounded ${shimmer}`} />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (variant === "stats") {
    return (
      <div className="py-28 lg:py-36 bg-navy">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="text-center space-y-4">
                <div className={`h-16 w-32 bg-white/10 rounded-lg mx-auto ${shimmer}`} />
                <div className={`h-6 w-48 bg-white/5 rounded-lg mx-auto ${shimmer}`} />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (variant === "cta") {
    return (
      <div className="py-28 lg:py-36 bg-cream">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className={`h-12 w-96 max-w-full bg-muted/40 rounded-lg mx-auto mb-6 ${shimmer}`} />
          <div className={`h-6 w-80 max-w-full bg-muted/30 rounded-lg mx-auto mb-8 ${shimmer}`} />
          <div className={`h-14 w-48 bg-muted/50 rounded-full mx-auto ${shimmer}`} />
        </div>
      </div>
    );
  }

  // Default variant
  return (
    <div className="py-28 lg:py-36 bg-cream">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-6">
            <div className={`h-8 w-32 bg-muted/50 rounded-full ${shimmer}`} />
            <div className={`h-12 w-full bg-muted/40 rounded-lg ${shimmer}`} />
            <div className="space-y-3">
              <div className={`h-5 w-full bg-muted/30 rounded ${shimmer}`} />
              <div className={`h-5 w-5/6 bg-muted/30 rounded ${shimmer}`} />
              <div className={`h-5 w-4/6 bg-muted/30 rounded ${shimmer}`} />
            </div>
          </div>
          <div className={`h-[400px] bg-muted/30 rounded-3xl ${shimmer}`} />
        </div>
      </div>
    </div>
  );
};

export default SectionSkeleton;
